/**
 * Purpose: Phase-specific content components rendered inside the GameplayPage game area.
 * Owns: Question phase (card + answer buttons), answer feedback phase, dice roll phase,
 *       and turn transition interstitial.
 * Notes: Extracted from GameplayPage to keep the orchestrator under the 500-line limit (CS-15).
 *        These are private sub-components — only imported by GameplayPage.
 *        All colors reference theme tokens (CS-22).
 */

import { QuestionCard } from '../components/game/QuestionCard';
import { AnswerButtons } from '../components/game/AnswerButtons';
import { TeachersDesk } from '../components/game/TeachersDesk';
import { DiceRoll } from '../components/game/DiceRoll';
import type { DiceValue } from '../engine/diceRoller';
import type { Question } from '../types/question';

// _______________________________________________
//              QUESTION PHASE
// _______________________________________________

export function QuestionPhaseContent({
  question,
  selectedAnswer,
  onSelectAnswer,
  hintUsed,
  eliminatedOption,
  onRequestHelp,
}: {
  question: Question;
  selectedAnswer: number | null;
  onSelectAnswer: (index: number) => void;
  /** Whether the Teacher's Desk hint has been used for this question */
  hintUsed: boolean;
  /** Index of the eliminated wrong answer (MC only, null if not yet used) */
  eliminatedOption: number | null;
  /** Called when the player taps "Teacher's Desk" to request help */
  onRequestHelp: () => void;
}) {
  return (
    <div style={phaseStyles.container}>
      <QuestionCard question={question} />

      {/* Teacher's Desk — ask for help before answering */}
      <TeachersDesk
        used={hintUsed}
        disabled={selectedAnswer !== null}
        hintText={hintUsed ? question.hint : null}
        onRequestHelp={onRequestHelp}
      />

      <AnswerButtons
        options={question.options}
        questionType={question.type}
        selectedAnswer={selectedAnswer}
        correctAnswer={question.correctAnswer}
        showResult={false}
        onSelectAnswer={onSelectAnswer}
        disabled={selectedAnswer !== null}
        eliminatedOption={eliminatedOption}
      />
    </div>
  );
}

// _______________________________________________
//              ANSWER FEEDBACK PHASE
// _______________________________________________

export function FeedbackPhaseContent({
  wasCorrect,
  question,
  selectedAnswer,
  onContinue,
}: {
  wasCorrect: boolean | null;
  question: Question;
  selectedAnswer: number | null;
  /** Called when the player taps "Continue" to advance past feedback. */
  onContinue: () => void;
}) {
  return (
    <div className="banner-pop" style={phaseStyles.container}>
      <p
        className="chalk-text-bright"
        style={{
          ...phaseStyles.feedbackText,
          color: wasCorrect ? 'var(--correct)' : 'var(--incorrect)',
        }}
        role="alert"
      >
        {wasCorrect ? 'Correct!' : 'Wrong!'}
      </p>

      {/* Show the question with answer reveal styling */}
      <QuestionCard question={question} />
      <AnswerButtons
        options={question.options}
        questionType={question.type}
        selectedAnswer={selectedAnswer}
        correctAnswer={question.correctAnswer}
        showResult={true}
        onSelectAnswer={() => {}}
        disabled={true}
      />

      {/* Explanation text */}
      {question.explanation && (
        <p className="chalk-text" style={phaseStyles.explanationText}>
          {question.explanation}
        </p>
      )}

      {/* Manual continue — lets the player read feedback at their own pace */}
      <button
        className="btn-gold"
        style={phaseStyles.continueButton}
        onClick={onContinue}
        aria-label="Continue"
      >
        Continue
      </button>
    </div>
  );
}

// _______________________________________________
//              DICE ROLL PHASE
// _______________________________________________

export function DiceRollPhaseContent({
  diceValue,
  isRolling,
  onRollComplete,
}: {
  diceValue: DiceValue | null;
  isRolling: boolean;
  onRollComplete: () => void;
}) {
  return (
    <div style={phaseStyles.container}>
      <p className="chalk-text-bright" style={phaseStyles.dicePromptText}>
        Roll for points!
      </p>
      <DiceRoll value={diceValue} isRolling={isRolling} onRollComplete={onRollComplete} />
    </div>
  );
}

// _______________________________________________
//              TURN TRANSITION
// _______________________________________________

export function TurnTransitionContent({ nextPlayerName }: { nextPlayerName: string }) {
  return (
    <div className="banner-pop" style={phaseStyles.container}>
      <p className="chalk-text-bright" style={phaseStyles.transitionText}>
        Next up: {nextPlayerName}
      </p>
    </div>
  );
}

// _______________________________________________
//              STYLES
// _______________________________________________

const phaseStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    padding: '1rem',
    textAlign: 'center' as const,
    width: '100%',
  },
  feedbackText: {
    fontSize: '2rem',
    lineHeight: 1.1,
  },
  transitionText: {
    fontSize: '1.6rem',
    lineHeight: 1.2,
  },
  dicePromptText: {
    fontSize: '1.4rem',
    lineHeight: 1.2,
    color: 'var(--accent-gold)',
  },
  explanationText: {
    fontSize: '0.95rem',
    lineHeight: 1.5,
    opacity: 0.8,
    maxWidth: '400px',
    fontFamily: 'var(--font-family-handwriting)',
  },
  continueButton: {
    padding: '0.75rem 2rem',
    fontSize: '1.1rem',
    fontFamily: 'var(--font-family)',
    borderRadius: '10px',
    marginTop: '0.25rem',
  },
};
